import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Popup} from 'devextreme-react/popup';
import {Form, SimpleItem, RequiredRule} from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import {apiRequest} from 'api/async';
import {vars, FTD_TYPE_LOOKUP} from 'utils/variables';
import {toNumberArray, toCommaSeparatedString} from 'utils/functions';

const {APP_CODES: {SUCCESS}} = vars;
const INFO_MESSAGE_TIMEOUT = 15000;

const RouterMerchantPopup = ({merchant, haveEnoughRight, onClose}) => {
  const formRef = useRef(null);
  const [routerRecord, setRouterRecord] = useState(null);
  const [formData, setFormData] = useState({
    MerchantID: merchant?.ID ?? '',
    RouterMerchantID: null,
    FtdTypes: [],
    FtdCnt: null,
  });
  const [infoMessage, setInfoMessage] = useState('');
  const infoTimerRef = useRef(null);

  const showInfoMessage = useCallback((message) => {
    setInfoMessage(message);
    if (infoTimerRef.current) {
      clearTimeout(infoTimerRef.current);
    }
    infoTimerRef.current = setTimeout(() => {
      setInfoMessage('');
      infoTimerRef.current = null;
    }, INFO_MESSAGE_TIMEOUT);
  }, []);

  const loadData = useCallback(async () => {
    if (!merchant?.ID) return;

    try {
      const response = await apiRequest({
        operation: 'RouterMerchant/List',
        data: {
          Filters: {
            MerchantID: {'=': merchant.ID},
          },
          Page: -1,
          RowsPerPage: 1,
          Columns: ['ID', 'MerchantID', 'RouterMerchantID', 'S3MerchantApiProfileID', 'FtdTypes', 'FtdCnt'],
          Sorts: ['ID'],
        },
      });

      if (response?.data?.ResponseCode !== SUCCESS) {
        notify(response?.data?.ResponseText || 'Ошибка загрузки', 'error', 3000);
        return;
      }

      const items = response?.data?.Response?.RouterMerchant || [];
      const nextRecord = Array.isArray(items) && items.length ? items[0] : null;

      setRouterRecord(nextRecord);
      setFormData({
        MerchantID: merchant.ID,
        RouterMerchantID: nextRecord?.RouterMerchantID ?? null,
        FtdTypes: toNumberArray(nextRecord?.FtdTypes),
        FtdCnt: nextRecord?.FtdCnt ?? null,
      });

      if (!nextRecord) {
        showInfoMessage('Настроенного роутинга нет');
      }
    } catch (error) {
      notify('Ошибка сети', 'error', 3000);
    }
  }, [merchant?.ID, showInfoMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (infoTimerRef.current) {
        clearTimeout(infoTimerRef.current);
      }
    };
  }, []);

  const isAddMode = useMemo(() => !routerRecord, [routerRecord]);

  const handleAdd = async () => {
    const validation = formRef.current?.instance?.().validate?.();
    if (validation && !validation.isValid) return;

    try {
      const response = await apiRequest({
        operation: 'RouterMerchant/Ins',
        data: {
          Params: {
            MerchantID: formData.MerchantID,
            RouterMerchantID: formData.RouterMerchantID,
            FtdTypes: toCommaSeparatedString(formData.FtdTypes),
            FtdCnt: formData.FtdCnt,
          },
        },
      });

      if (response?.data?.ResponseCode === SUCCESS) {
        onClose();
        return;
      }

      notify(response?.data?.ResponseText || 'Ошибка сохранения', 'error', 3000);
    } catch (error) {
      notify('Ошибка сети', 'error', 3000);
    }
  };

  const handleModify = async () => {
    if (!routerRecord?.ID) return;

    const validation = formRef.current?.instance?.().validate?.();
    if (validation && !validation.isValid) return;

    try {
      const response = await apiRequest({
        operation: 'RouterMerchant/Mod',
        data: {
          Params: {
            ID: routerRecord.ID,
            MerchantID: formData.MerchantID,
            RouterMerchantID: formData.RouterMerchantID,
            FtdTypes: toCommaSeparatedString(formData.FtdTypes),
            FtdCnt: formData.FtdCnt,
          },
        },
      });

      if (response?.data?.ResponseCode === SUCCESS) {
        onClose();
        return;
      }

      notify(response?.data?.ResponseText || 'Ошибка сохранения', 'error', 3000);
    } catch (error) {
      notify('Ошибка сети', 'error', 3000);
    }
  };

  const handleDelete = async () => {
    if (!routerRecord?.ID) return;

    try {
      const response = await apiRequest({
        operation: 'RouterMerchant/Del',
        data: {
          Params: {
            ID: routerRecord.ID,
          },
        },
      });

      if (response?.data?.ResponseCode === SUCCESS) {
        await loadData();
        return;
      }

      notify(response?.data?.ResponseText || 'Ошибка удаления', 'error', 3000);
    } catch (error) {
      notify('Ошибка сети', 'error', 3000);
    }
  };

  return (
    <Popup
      visible={true}
      onHiding={onClose}
      hideOnOutsideClick={false}
      showCloseButton={true}
      title="Мерчант-Роутинг-Каскадинг"
      width={520}
      height={'auto'}
      maxHeight={'90vh'}
      container={'#main-content'}
    >
      <div style={{padding: '16px'}}>
        {infoMessage && (
          <div className="router-info-message">
            {infoMessage}
          </div>
        )}

        <Form
          ref={formRef}
          formData={formData}
          labelLocation="left"
          colCount={1}
          onFieldDataChanged={(e) => {
            if (!e?.dataField) return;
            setFormData((prev) => ({...prev, [e.dataField]: e.value}));
          }}
        >
          <SimpleItem
            dataField="MerchantID"
            label={{text: 'ID мерчанта'}}
            editorType="dxNumberBox"
            editorOptions={{readOnly: true}}
          />

          <SimpleItem
            dataField="RouterMerchantID"
            label={{text: 'ID роутинга'}}
            editorType="dxNumberBox"
            editorOptions={{readOnly: !haveEnoughRight}}
          >
            {haveEnoughRight && isAddMode && <RequiredRule message="ID роутинга обязателен"/>}
          </SimpleItem>

          <SimpleItem
            dataField="FtdTypes"
            label={{text: 'FTD:Типы платежей'}}
            editorType="dxTagBox"
            editorOptions={{
              readOnly: !haveEnoughRight,
              dataSource: FTD_TYPE_LOOKUP,
              valueExpr: 'id',
              displayExpr: 'name',
              showSelectionControls: true,
              showClearButton: true,
              applyValueMode: 'useButtons',
            }}
          />

          <SimpleItem
            dataField="FtdCnt"
            label={{text: 'FTD:Кол-во платежей'}}
            editorType="dxNumberBox"
            editorOptions={{
              readOnly: !haveEnoughRight,
            }}
          />
        </Form>

        <div className="router-popup-actions">
          {haveEnoughRight && routerRecord && (
            <Button
              text="Изменить"
              type="default"
              stylingMode="contained"
              onClick={handleModify}
            />
          )}
          {haveEnoughRight && routerRecord && (
            <Button
              text="Удалить"
              type="danger"
              stylingMode="contained"
              onClick={handleDelete}
            />
          )}
          {haveEnoughRight && !routerRecord && (
            <Button
              text="Добавить"
              type="success"
              stylingMode="contained"
              onClick={handleAdd}
            />
          )}
          <Button
            text="Отменить"
            onClick={onClose}
          />
        </div>
      </div>
    </Popup>
  );
};

export default RouterMerchantPopup;
